import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, Loader2, Bell } from "lucide-react";
import { PasswordField } from "../components/PasswordField";
import { PasswordGenerator } from "../components/PasswordGenerator";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { useLang } from "../context/LanguageContext";

const formatDetail = (detail) => {
  if (!detail) return "Please try again";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

const Signup = () => {
  const navigate = useNavigate();
  const { registerShopper } = useAuth();
  const { toast } = useToast();
  const { t } = useLang();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [acceptWeak, setAcceptWeak] = useState(false);
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await registerShopper({ ...f, acceptWeak, acceptTerms: agree });
      toast({ title: t("auth.welcomeToLyte"), description: t("auth.signupMsg") });
      const pending = localStorage.getItem("lp-post-login-grab");
      if (pending) { navigate(`/deal/${pending}`); return; }
      navigate("/browse");
    } catch (err) {
      toast({ title: t("auth.signupFailed"), description: formatDetail(err.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  const input = "w-full bg-transparent outline-none text-sm text-foreground";
  const box = "flex items-center gap-3 border border-border rounded-xl px-4 py-3";

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/50 px-5 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex flex-col items-center gap-2 mb-6">
          <img src="/lp-logo.webp" alt="LytePress" className="h-16 w-16 rounded-full border border-border shadow-sm" />
          <span className="text-xl font-extrabold text-foreground">LytePress</span>
        </Link>
        <div className="bg-card border border-border rounded-2xl shadow-xl p-8">
          <span className="mx-auto mb-3 h-12 w-12 rounded-2xl bg-[#e7f6ec] dark:bg-[#16341f] flex items-center justify-center"><Bell size={22} className="text-[#16a34a]" /></span>
          <h1 className="text-2xl font-extrabold text-foreground text-center mb-1">{t("auth.signupTitle")}</h1>
          <p className="text-sm text-muted-foreground text-center mb-6">{t("auth.signupMsg")}</p>
          <form onSubmit={submit} className="space-y-4">
            <div className={box}><User size={18} className="text-muted-foreground" /><input required value={f.name} onChange={set("name")} placeholder={t("auth.yourName")} className={input} data-testid="signup-name" /></div>
            <div className={box}><Mail size={18} className="text-muted-foreground" /><input type="email" required value={f.email} onChange={set("email")} placeholder={t("auth.email")} className={input} data-testid="signup-email" /></div>
            <PasswordField value={f.password} onChange={set("password")} role="shopper"
              email={f.email} name={f.name} testid="signup-password"
              acceptWeak={acceptWeak} onAcceptWeak={setAcceptWeak} />
            <PasswordGenerator onPick={(pw) => { setAcceptWeak(false); setF((p) => ({ ...p, password: pw })); }} testid="signup-pw-gen" />
            <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="signup-terms-label">
              <input type="checkbox" required checked={agree} onChange={(e) => setAgree(e.target.checked)}
                data-testid="signup-terms-checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-border accent-[#16a34a]" />
              <span className="text-xs text-muted-foreground leading-snug">
                {t("auth.agreeCheckbox")} <Link to="/terms" target="_blank" className="text-[#16a34a] font-semibold hover:underline">{t("footer.terms")}</Link> {t("auth.and")} <Link to="/privacy" target="_blank" className="text-[#16a34a] font-semibold hover:underline">{t("footer.privacy")}</Link>.
              </span>
            </label>
            <button disabled={loading || !agree} data-testid="signup-submit" className="w-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">{loading && <Loader2 className="animate-spin" size={18} />} {t("mydeals.createAccount")}</button>
          </form>
          <p className="mt-4 text-center text-sm text-muted-foreground">{t("auth.haveAccount")} <Link to="/login" className="text-[#16a34a] font-semibold hover:underline">{t("auth.logIn")}</Link></p>
          <p className="mt-2 text-center text-sm text-muted-foreground">{t("auth.areBusiness")} <Link to="/register" className="text-[#16a34a] font-semibold hover:underline">{t("auth.registerBusiness")}</Link></p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
